import React from 'react';
import {
  Chip, createStyles, DialogActions, Theme, WithStyles, withStyles,
} from '@material-ui/core';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';

import CancelButton from 'hcla-web-frontend-primitives/app/components/primitives/Buttons/CancelButton';

import { mean } from 'mathjs';
import Grid from '@material-ui/core/Grid';

import Plot from 'react-plotly.js';
import { NodeLabel } from '../NodeLabel/NodeLabel';
import { QRLabelMapJSON } from '../../Quantification/QuantificationResultJSON';
import StatsTable from './StatsTable';

const percentile = require('percentile');

const styles = createStyles({
  rowItem: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});


export interface UncertainProbabilityChipProps extends InjectedIntlProps, WithStyles<typeof styles> {
  data: number[];
  dialogProps?: DialogProps;
  map: QRLabelMapJSON;
  name: string;
  theme: Theme;
  label?: string;
}

interface State {
  portalOpen: boolean;
}

class UncertainProbabilityChip extends React.Component<UncertainProbabilityChipProps, State> {
  private readonly nodeLabel: NodeLabel;

  private readonly mean: number;

  private readonly p5: number;

  private readonly p50: number;

  private readonly p95: number;

  constructor(props: UncertainProbabilityChipProps) {
    super(props);
    this.state = {
      portalOpen: false,
    };
    const { map, name, data } = props;
    this.mean = mean(data);
    this.p5 = percentile(5, data);
    this.p50 = percentile(50, data);
    this.p95 = percentile(95, data);
    this.nodeLabel = NodeLabel.buildWithDefaults(map, name);
  }

  private handleClose = (e: React.SyntheticEvent<{}, Event>) => {
    this.setState({ portalOpen: false });
  };

  private handleOpen = (e: React.SyntheticEvent<{}, Event>) => {
    this.setState({ portalOpen: true });
  };

  render() {
    const { portalOpen } = this.state;
    const {
      dialogProps, data, classes: { rowItem }, theme, intl, label, className
    } = this.props;
    const { label: { name, description } } = this.nodeLabel;
    return (
      <React.Fragment>
        <div className={rowItem}>
          <Chip
            variant="default"
            color="secondary"
            label={label? `${label}: ${(this.mean).toExponential(3)}` : (this.mean).toExponential(3)}
            onClick={this.handleOpen}
            className={className}
          />
        </div>
        <Dialog
          {...dialogProps}
          open={portalOpen}
          fullWidth
          maxWidth="md"
          onClose={this.handleClose}
          disablePortal
        >
          <DialogTitle>
            <Grid
              container
              alignItems="center"
              justify="space-between"
            >
              <Grid item>
                <Typography variant="h5" color="textPrimary">{name}</Typography>
                <Typography variant="subtitle1" color="textSecondary">{description}</Typography>
              </Grid>
              <Grid item>
                <StatsTable
                  data={[
                    {
                      title: intl.formatMessage({ id: 'statistics.mean' }),
                      label: (this.mean).toExponential(3),
                      text: this.mean,
                    },
                    {
                      title: intl.formatMessage({ id: 'statistics.median' }),
                      label: (this.p50).toExponential(3),
                      text: this.p50,
                    },
                    {
                      title: intl.formatMessage({ id: 'statistics.p5' }),
                      label: (this.p5).toExponential(3),
                      text: this.p5,
                    },
                    {
                      title: intl.formatMessage({ id: 'statistics.p95' }),
                      label: (this.p95).toExponential(3),
                      text: this.p95,
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <Plot
              style={{ width: '100%' }}
              config={{
                displaylogo: false,
                autosizable: true,
                responsive: true,
                toImageButtonOptions: {
                  format: 'png', // one of png, svg, jpeg, webp
                  filename: name,
                  width: 640,
                  height: 360,
                  scale: 2,
                },
              }}
              data={
                [
                  {
                    x: data,
                    name,
                    type: 'histogram',
                    boxpoints: 'all',
                    zeroline: false,
                    boxmean: 'sd',
                    opacity: 0.75,
                    histnorm: 'probability',
                    marker: {
                      color: theme.palette.primary.light,
                      line: {
                        color: theme.palette.primary.main,
                        width: 1.5,
                      },
                    },
                  },
                ]
              }
              layout={{
                barmode: 'overlay',
                paper_bgcolor: 'transparent',
                plot_bgcolor: 'transparent',
                font: {
                  color: theme.palette.text.primary,
                  family: theme.typography.fontFamily,
                  size: theme.typography.fontSize,
                },
                title: name,
                xaxis: {
                  color: theme.palette.text.secondary,
                  gridcolor: theme.palette.text.secondary,
                  title: intl.formatMessage({ id: 'ProbabilityPlot.axis.x.title' }),
                  type: 'linear',
                  autorange: true,
                },
                yaxis: {
                  color: theme.palette.text.secondary,
                  gridcolor: theme.palette.text.secondary,
                  title: intl.formatMessage({ id: 'ProbabilityPlot.axis.y.title' }),
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <CancelButton
              variant="outlined"
              onClick={this.handleClose}
              size="large"
            >
              {intl.formatMessage({ id: 'Close' })}
            </CancelButton>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
}

export default withStyles(styles, { withTheme: true })(injectIntl(UncertainProbabilityChip));
